import { useMutation } from '@tanstack/react-query';
import { Check as CheckIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DockedFooter } from '@/components/DockedFooter';
import { redeemLoyalty } from '@/api/loyalty';
import { formatMoney } from '@/lib/money';
import { useTranslation } from '@/i18n';
import type { LoyaltyConfiguration, LoyaltyOffers, LoyaltyRedeemable, LoyaltyRedemption } from '@/api/types';

interface LoyaltyAccountProps {
    configuration: LoyaltyConfiguration;
    offers: LoyaltyOffers;
    accountId: string;
    signInToken: string | null;
    accessToken: string;
    currency: string;
    locale: string;
    /** Shown for an offer or reward with no picture of its own, which most have not. */
    placeholderUrl: string | null;
    /**
     * The check has moved at the POS. What the guest redeemed is not passed back: the server recorded it
     * when it applied the offers, and committing it happens inside payment completion where the money is
     * known to have moved. This client used to carry the ids through checkout for a finalize call it made
     * itself, which a guest who closed their tab on the receipt simply skipped.
     */
    onRedeemed: (result: LoyaltyRedemption) => void;
    onSkip: () => void;
}

/**
 * The guest's account, and what they can put on this check.
 *
 * Offers and rewards are shown as two lists because the vendor sends two and a guest reads them differently —
 * an offer was aimed at them, a reward costs points they have saved. The redemption limit counts across both,
 * which is what the handheld does and the only sensible reading of "per order".
 *
 * The action is docked at the foot of the viewport and the lists scroll under it, like the check screen: an
 * account with a dozen rewards would otherwise push the only way forward off the bottom of the page.
 */
export function LoyaltyAccount({
    configuration,
    offers,
    accountId,
    signInToken,
    accessToken,
    currency,
    locale,
    placeholderUrl,
    onRedeemed,
    onSkip,
}: LoyaltyAccountProps) {
    const { t } = useTranslation();
    const [selected, setSelected] = useState<string[]>([]);

    const money = (minorUnits: number) => formatMoney(minorUnits, currency, locale);

    const limit = configuration.maxRedemptionsPerOrder;
    const atLimit = limit !== null && selected.length >= limit;

    const redeem = useMutation({
        mutationFn: () => redeemLoyalty(accessToken, accountId, signInToken, selected),
        onSuccess: (result) => onRedeemed(result),
    });

    const toggle = (id: string) => {
        setSelected((current) =>
            current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
        );
    };

    const nothingToRedeem = offers.offers.length === 0 && offers.rewards.length === 0;

    return (
        <>
            {/* pb-40 keeps the last card clear of the docked footer. */}
            <div className="flex flex-col gap-6 pb-40">
                {offers.account && (
                    <div className="flex flex-col items-center gap-1 text-center">
                        <h1 className="text-2xl font-semibold tracking-tight text-balance">
                            {t('loyalty.greeting', { name: offers.account.name })}
                        </h1>
                        <p className="tabular text-lg font-medium text-muted-foreground">
                            {t('loyalty.points', { points: offers.account.pointsBalance })}
                        </p>
                    </div>
                )}

                {nothingToRedeem ? (
                    <p className="text-center text-[0.9375rem] text-muted-foreground text-pretty">
                        {t('loyalty.nothingAvailable')}
                    </p>
                ) : (
                    <>
                        <RedeemableList
                            heading={t('loyalty.offers')}
                            items={offers.offers}
                            selected={selected}
                            atLimit={atLimit}
                            disabled={redeem.isPending}
                            placeholderUrl={placeholderUrl}
                            money={money}
                            onToggle={toggle}
                        />
                        <RedeemableList
                            heading={t('loyalty.rewards')}
                            items={offers.rewards}
                            selected={selected}
                            atLimit={atLimit}
                            disabled={redeem.isPending}
                            placeholderUrl={placeholderUrl}
                            money={money}
                            onToggle={toggle}
                        />

                        {/*
                          * Announced rather than static, because it appears as a consequence of the guest's
                          * last tap and explains why the other cards just went quiet.
                          */}
                        {atLimit && (
                            <p role="status" className="text-center text-[0.9375rem] text-muted-foreground text-pretty">
                                {t('loyalty.limitReached')}
                            </p>
                        )}
                    </>
                )}

            </div>

            <DockedFooter>
                <div className="flex flex-col gap-2">
                    {/*
                      * The failure sits with the button that caused it, not at the end of the list.
                      *
                      * It was below the cards, which is where the content ends but not where the guest is
                      * looking — with the action docked, anyone scrolled up would never see it. An error from
                      * pressing a control belongs beside that control.
                      */}
                    {redeem.isError && (
                        <p role="alert" className="text-[0.9375rem] font-medium text-destructive text-pretty">
                            {t('loyalty.redeemFailed')}
                        </p>
                    )}

                    {selected.length > 0 && (
                        <Button
                            size="lg"
                            className="h-[3.25rem] w-full rounded-xl text-base font-semibold"
                            onClick={() => redeem.mutate()}
                            disabled={redeem.isPending}
                        >
                            {redeem.isPending ? t('loyalty.working') : t('loyalty.redeem')}
                        </Button>
                    )}

                    <Button
                        variant={selected.length > 0 ? 'ghost' : 'default'}
                        size="lg"
                        className="h-[3.25rem] w-full rounded-xl text-base font-semibold"
                        onClick={onSkip}
                        disabled={redeem.isPending}
                    >
                        {selected.length > 0 ? t('loyalty.skip') : t('loyalty.continue')}
                    </Button>
                </div>
            </DockedFooter>
        </>
    );
}

interface RedeemableListProps {
    heading: string;
    items: LoyaltyRedeemable[];
    selected: string[];
    atLimit: boolean;
    disabled: boolean;
    placeholderUrl: string | null;
    money: (minorUnits: number) => string;
    onToggle: (id: string) => void;
}

function RedeemableList({
    heading,
    items,
    selected,
    atLimit,
    disabled,
    placeholderUrl,
    money,
    onToggle,
}: RedeemableListProps) {
    const { t } = useTranslation();

    if (items.length === 0) return null;

    return (
        <section aria-label={heading} className="flex flex-col gap-2">
            <h2 className="font-semibold tracking-tight">{heading}</h2>

            {/*
              * A grid rather than a stack, for `auto-rows-fr`: every card in the list takes the height of the
              * tallest, so a reward with two lines of terms does not make the one above it look truncated.
              * The description is clamped at three lines, which is what bounds that height.
              */}
            <ul className="grid auto-rows-fr grid-cols-1 gap-3">
                {items.map((item) => {
                    const isSelected = selected.includes(item.id);

                    return (
                        <li key={item.id}>
                            {/*
                              * A checkbox, not a button. Several can be chosen at once and each is
                              * independently on or off, which is what a checkbox means — and it gets the
                              * "checked"/"not checked" state announced without any ARIA of ours.
                              */}
                            <label
                                className={[
                                    'flex h-full cursor-pointer flex-col overflow-hidden rounded-xl border transition-colors',
                                    isSelected
                                        ? 'border-primary bg-primary/10'
                                        : 'border-border bg-background',
                                    // Only the ones that cannot be added. Deselecting stays available at the
                                    // limit, or a guest who picked wrong would be stuck with it.
                                    !isSelected && (atLimit || disabled) ? 'opacity-50' : '',
                                    'has-[:focus-visible]:outline has-[:focus-visible]:outline-3',
                                    'has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-foreground',
                                ].join(' ')}
                            >
                                {/*
                                  * Visually hidden but still a real checkbox, focusable and announced. The
                                  * selected state is shown by the tick over the image instead, where the
                                  * handheld puts it - with a graphic this size, a control tucked beside the
                                  * text reads as an afterthought next to the card itself being the target.
                                  */}
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={disabled || (atLimit && !isSelected)}
                                    onChange={() => onToggle(item.id)}
                                    className="sr-only"
                                />

                                <span className="relative">
                                    <RedeemableImage item={item} placeholderUrl={placeholderUrl} />

                                    {isSelected && (
                                        <span
                                            aria-hidden="true"
                                            className="absolute top-2 right-2 flex size-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm"
                                        >
                                            <CheckIcon className="size-4" strokeWidth={3} />
                                        </span>
                                    )}
                                </span>

                                <span className="flex flex-1 flex-col gap-0.5 p-3">
                                    <span className="font-medium text-pretty">{item.name}</span>

                                    {/*
                                      * Three lines, then an ellipsis. Terms run long, and a guest deciding
                                      * between two rewards needs them to line up rather than to read every
                                      * word — the full text belongs to the offer, not here.
                                      *
                                      * The slot is always three lines tall, even for an item with no
                                      * description. That is what makes every card the same height in BOTH
                                      * lists: `auto-rows-fr` equalises rows within one grid, and offers and
                                      * rewards are two.
                                      */}
                                    <span className="line-clamp-3 min-h-[3.9375rem] text-[0.8125rem] text-muted-foreground">
                                        {item.description}
                                    </span>

                                    {/*
                                      * What it is worth on the left, what it costs on the right — the two
                                      * halves of the same decision, read in that order. One line rather than
                                      * stacked, so an offer that costs no points leaves the right side empty
                                      * instead of sitting a line shorter than the reward beside it.
                                      */}
                                    <span className="tabular mt-auto flex items-baseline justify-between gap-2 pt-1">
                                        <span className="text-[0.9375rem] font-medium">
                                            {money(item.amountCents)}
                                        </span>

                                        {item.pointValue > 0 && (
                                            <span className="text-[0.8125rem] text-muted-foreground">
                                                {t('loyalty.pointCost', { points: String(item.pointValue) })}
                                            </span>
                                        )}
                                    </span>
                                </span>
                            </label>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

/**
 * The picture at the top of a card: the offer's own, or the brand's placeholder.
 *
 * 3:2 and full width, matching the handheld — it sizes the image block as `width * 2/3`, and a guest who sees
 * both surfaces should not find the same reward laid out two different ways.
 *
 * The two images are fitted differently, also as on the handheld, and it matters. An offer's own picture is a
 * photograph and is cropped to fill: letterboxing it would leave bars either side of every card. The
 * placeholder is a logo and is contained at its natural size: cropping a logo cuts the brand in half, which
 * looks like a fault rather than a design.
 *
 * A brand that uploaded no placeholder gets no image block at all rather than an empty grey band.
 */
function RedeemableImage({
    item,
    placeholderUrl,
}: {
    item: LoyaltyRedeemable;
    placeholderUrl: string | null;
}) {
    const [failed, setFailed] = useState(false);
    const own = !failed && item.imageUrl !== null;
    const src = own ? item.imageUrl : placeholderUrl;

    if (!src) return null;

    return (
        <span className="flex aspect-[3/2] w-full items-center justify-center overflow-hidden bg-muted">
            <img
                src={src}
                // Decorative: the name and price below say everything this conveys, and announcing "Free
                // dessert" twice per card is worse than saying it once.
                alt=""
                onError={() => setFailed(true)}
                className={own ? 'size-full object-cover' : 'max-h-full max-w-[70%] object-contain p-3'}
            />
        </span>
    );
}
